import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clientService } from '../../application/ClientService';
import { driverService } from '../../application/DriverService';
import { stopService } from '../../application/StopService';
import { vehicleService } from '../../application/VehicleService';

import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useRetrieveActivePlan } from '../plan/useRetrieveActivePlan';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

type UpdateConstraintsParams = {
  ids: string[];
  constraints: Record<string, any>;
};

type UseUpdateConstraintsProps = ServiceResponse<({ ids, constraints }: UpdateConstraintsParams) => void, void>;

// Hooks act as 'Adapter' layer.
export const useUpdateConstraints = ({
  typeData,
}: {
  typeData: `drivers` | `stops` | `vehicles` | `clients`;
}): UseUpdateConstraintsProps => {
  const queryClient = useQueryClient();
  const { data: activePlan } = useRetrieveActivePlan();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const { isLoading, isSuccess, isError, mutate } = useMutation<string[], unknown, UpdateConstraintsParams>(
    async ({ ids, constraints }: UpdateConstraintsParams): Promise<string[]> => {
      switch (typeData) {
        case `stops`:
          await stopService.updateConstraints({ stopIds: ids, constraints });
          break;
        case `drivers`:
          await driverService.updateConstraints({ driverIds: ids, constraints });
          break;
        case `vehicles`:
          await vehicleService.updateConstraints({ vehicleIds: ids, constraints });
          break;
        case `clients`:
          await clientService.updateConstraints({ clientIds: ids, constraints });
          break;
      }
      return ids;
    },
    {
      onSuccess: async (ids: string[]) => {
        switch (typeData) {
          case `stops`:
            ids.map(async (id) => {
              await queryClient.invalidateQueries([`planner_stop`, id]);
            });
            await queryClient.invalidateQueries([`planner_search_stops`]);

            if (activePlan) {
              await queryClient.invalidateQueries([`planner_active_plan_stops`, activePlan.id]);
            }
            break;
          case `drivers`:
            ids.map(async (id) => {
              await queryClient.invalidateQueries([`planner_driver`, id]);
            });
            if (activePlan) {
              await queryClient.invalidateQueries([`planner_active_plan_drivers`, activePlan.id]);
            }
            break;
          case `clients`:
            ids.map(async (id) => {
              await queryClient.invalidateQueries([`planner_client`, id]);
            });
            // TODO: INVALIDATE CLIENTS LIST
            break;
          case `vehicles`:
            ids.map(async (id) => {
              await queryClient.invalidateQueries([`planner_vehicle`, id]);
            });
            // TODO: INVALIDATE VEHICLES LIST
            break;
        }
      },
      onError: (err: any) => {
        console.info(`Update Constraints Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    isLoading: isLoading,
    success: isSuccess,
    run: ({ ids, constraints }: UpdateConstraintsParams) => mutate({ ids, constraints }),
  };
};
